// Lone Rider — Creators application form
//
// Drives sections/creators-apply.liquid against the creators application service.
// The service's upload protocol is the contract, step by step:
//
//   1. GET  /api/applications/status    open: false hides the form
//   2. POST /api/applications/init      file list in, creator_id + one signed URL per file out
//   3. PUT  each file to its signed URL (XMLHttpRequest, for real upload progress)
//   4. POST /api/applications/complete  form fields + the uploaded paths
//
// RETRYING
// What happens after an error is the protocol's table, nothing more:
//   complete 400, field errors only  uploads are kept: show the errors and resend
//                                    complete with the same creator_id and files
//   complete 400, files.* errors     uploads were deleted: next send starts at init
//   complete 500                     uploads were deleted: next send starts at init
//   complete 409                     already accepted: success
//   init / complete 503              applications closed: show the closed message
//   network error, 413, anything else  message + Try again, which resumes from the
//                                    step that failed
// Changing the files after they were uploaded also starts again at init, because a
// resend must carry exactly the files init returned paths for.
//
// TEXT
// This file holds no customer-facing text. Every string comes from the JSON tag in the
// section (rendered with `t`), repeated markup from its <template> tags, and country
// names from Intl.DisplayNames in the page language.
//
// PERSONAL DATA
// Kept only in the form itself and in this instance while the page is open. Nothing is
// written to storage or cookies; on success the form is removed from the page.
(() => {
  'use strict';

  // Officially assigned ISO 3166-1 alpha-2 codes (249): ALLOWED_COUNTRY_CODES from the
  // service's countries.ts, copied verbatim. The server rejects any other code.
  const COUNTRY_CODES = (
    'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ ' +
    'BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
    'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
    'DE DJ DK DM DO DZ ' +
    'EC EE EG EH ER ES ET ' +
    'FI FJ FK FM FO FR ' +
    'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY ' +
    'HK HM HN HR HT HU ' +
    'ID IE IL IM IN IO IQ IR IS IT ' +
    'JE JM JO JP ' +
    'KE KG KH KI KM KN KP KR KW KY KZ ' +
    'LA LB LC LI LK LR LS LT LU LV LY ' +
    'MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
    'NA NC NE NF NG NI NL NO NP NR NU NZ ' +
    'OM ' +
    'PA PE PF PG PH PK PL PM PN PR PS PT PW PY ' +
    'QA ' +
    'RE RO RS RU RW ' +
    'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ ' +
    'TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
    'UA UG UM US UY UZ ' +
    'VA VC VE VG VI VN VU ' +
    'WF WS ' +
    'YE YT ' +
    'ZA ZM ZW'
  ).split(' ');
  const COUNTRY_SET = new Set(COUNTRY_CODES);

  // The server's rules (upload protocol, `init` and `complete` tables).
  const MB = 1048576;
  const LIMITS = {
    name: 120,
    email: 254,
    introduction: 2000,
    gear: 1000,
    socialsMax: 10,
    url: 500,
    screenshotsMax: 8,
    screenshotBytes: 10 * MB,
    mediaPackBytes: 25 * MB
  };
  const PLATFORMS = ['instagram', 'youtube', 'tiktok', 'facebook', 'x', 'twitch', 'other'];
  const KIND_SCREENSHOT = 'analytics_screenshot';
  const KIND_MEDIA_PACK = 'media_pack';
  const SCREENSHOT_TYPES = ['image/png', 'image/jpeg'];
  const MEDIA_PACK_TYPE = 'application/pdf';

  // First bytes the server checks each file against. Checked here too, so a renamed
  // file is caught before it is uploaded rather than after.
  const SIGNATURES = {
    'image/png': [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    'image/jpeg': [0xff, 0xd8, 0xff],
    'application/pdf': [0x25, 0x50, 0x44, 0x46, 0x2d]
  };

  // Two at a time keeps a phone responsive and its memory flat on large files.
  const UPLOAD_CONCURRENCY = 2;
  // If the status check hangs, show the form anyway; the server decides on submit.
  const STATUS_TIMEOUT_MS = 8000;

  const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

  // [token] placeholders, the theme.strings convention.
  function fill(text, values) {
    return String(text).replace(/\[(\w+)\]/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
    );
  }

  function isHttpUrl(value) {
    try {
      const url = new URL(value);
      return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== '';
    } catch (e) {
      return false;
    }
  }

  function readHead(file, length) {
    const blob = file.slice(0, length);
    if (blob.arrayBuffer) return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(blob);
    });
  }

  function matchesSignature(file, mime) {
    const signature = SIGNATURES[mime];
    if (!signature) return Promise.resolve(false);
    return readHead(file, signature.length)
      .then((bytes) => signature.every((byte, i) => bytes[i] === byte))
      .catch(() => false);
  }

  // The "Other" option always sends this, whatever the page language. Its visible label
  // is the bikeOther string.
  const BIKE_OTHER_VALUE = 'Other';
  const TYPEAHEAD_RESET_MS = 600;
  const PAGE_STEP = 5;

  // The bike model dropdown: a button that opens a listbox (the ARIA collapsible
  // listbox pattern). Focus moves to the listbox while it is open; the active option
  // is tracked with aria-activedescendant. The value lives only in this instance.
  class BikePicker {
    constructor(field, models, otherLabel, onChange) {
      this.field = field;
      this.wrap = field.querySelector('[data-bike-picker]');
      this.button = field.querySelector('[data-bike-button]');
      this.valueEl = field.querySelector('[data-bike-value]');
      this.list = field.querySelector('[data-bike-list]');
      this.placeholder = this.valueEl.textContent;
      this.onChange = onChange;
      this.value = '';
      this.activeIndex = -1;
      this.typed = '';
      this.typedAt = 0;
      this.pointerInside = false;

      const values = models.filter((model) => model !== BIKE_OTHER_VALUE);
      this.options = values
        .map((value) => ({ value, label: value }))
        .concat([{ value: BIKE_OTHER_VALUE, label: otherLabel }]);
      this.render();

      this.onDocumentClick = (event) => {
        if (!this.wrap.contains(event.target)) this.close(false);
      };
      this.onResize = () => this.place();
      this.bind();
    }

    get isOpen() {
      return !this.list.hidden;
    }

    render() {
      const fragment = document.createDocumentFragment();
      this.options.forEach((option, i) => {
        const li = document.createElement('li');
        li.id = `${this.list.id}-${i}`;
        li.className = 'creators-apply__picker-option';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.textContent = option.label;
        option.el = li;
        fragment.appendChild(li);
      });
      this.list.appendChild(fragment);
    }

    bind() {
      // A label "for" a button would click it; focus it instead, like a native select.
      const label = this.field.querySelector('[data-bike-label]');
      label.addEventListener('click', (event) => {
        event.preventDefault();
        this.button.focus();
      });

      this.button.addEventListener('click', () => {
        if (this.isOpen) this.close(true);
        else this.open();
      });
      this.button.addEventListener('keydown', (event) => this.onButtonKey(event));
      this.list.addEventListener('keydown', (event) => this.onListKey(event));

      this.list.addEventListener('click', (event) => {
        const li = event.target.closest('[role="option"]');
        if (li) this.select(this.indexOf(li));
      });
      this.list.addEventListener('mousemove', (event) => {
        const li = event.target.closest('[role="option"]');
        if (li) this.setActive(this.indexOf(li), false);
      });

      // Tapping an option must not count as leaving the field, even where the tap moves
      // focus out of the listbox for a moment.
      const release = () => {
        this.pointerInside = false;
      };
      this.wrap.addEventListener('pointerdown', () => {
        this.pointerInside = true;
        document.addEventListener('pointerup', release, { once: true });
        document.addEventListener('pointercancel', release, { once: true });
      });
      this.wrap.addEventListener('focusout', (event) => {
        if (this.pointerInside) return;
        if (!event.relatedTarget || !this.wrap.contains(event.relatedTarget)) this.close(false);
      });
    }

    indexOf(li) {
      return this.options.findIndex((option) => option.el === li);
    }

    open(index) {
      if (this.isOpen) return;
      const selected = this.options.findIndex((option) => option.value === this.value);
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.place();
      this.setActive(typeof index === 'number' ? index : Math.max(selected, 0), 'center');
      this.list.focus({ preventScroll: true });
      document.addEventListener('click', this.onDocumentClick, true);
      window.addEventListener('resize', this.onResize);
    }

    close(focusButton) {
      document.removeEventListener('click', this.onDocumentClick, true);
      window.removeEventListener('resize', this.onResize);
      if (!this.isOpen) return;
      this.list.hidden = true;
      this.list.removeAttribute('aria-activedescendant');
      this.button.setAttribute('aria-expanded', 'false');
      this.wrap.classList.remove('creators-apply__picker--up');
      this.typed = '';
      if (focusButton) this.button.focus();
    }

    // Below the button; above it only when the list does not fit below and there is
    // more room above.
    place() {
      this.wrap.classList.remove('creators-apply__picker--up');
      const rect = this.button.getBoundingClientRect();
      const height = this.list.offsetHeight;
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      if (below < height && above > below) this.wrap.classList.add('creators-apply__picker--up');
    }

    // scroll: false (pointer), 'nearest' (keyboard) or 'center' (on open).
    setActive(index, scroll) {
      if (index < 0 || index >= this.options.length) return;
      if (this.activeIndex > -1 && this.options[this.activeIndex]) {
        this.options[this.activeIndex].el.classList.remove('is-active');
      }
      this.activeIndex = index;
      const el = this.options[index].el;
      el.classList.add('is-active');
      this.list.setAttribute('aria-activedescendant', el.id);
      if (!scroll) return;
      // Scrolls the list only, never the page (scrollIntoView would do both).
      const top = el.offsetTop;
      const bottom = top + el.offsetHeight;
      const view = this.list.clientHeight;
      if (scroll === 'center') {
        this.list.scrollTop = Math.max(0, top - (view - el.offsetHeight) / 2);
      } else if (top < this.list.scrollTop) {
        this.list.scrollTop = top;
      } else if (bottom > this.list.scrollTop + view) {
        this.list.scrollTop = bottom - view;
      }
    }

    select(index) {
      const option = this.options[index];
      if (!option) return;
      const changed = option.value !== this.value;
      this.setValue(option.value);
      this.close(true);
      if (changed) this.onChange();
    }

    setValue(value) {
      const option = this.options.find((o) => o.value === value);
      this.value = option ? option.value : '';
      this.options.forEach((o) => o.el.setAttribute('aria-selected', o === option ? 'true' : 'false'));
      this.valueEl.textContent = option ? option.label : this.placeholder;
    }

    reset() {
      this.close(false);
      this.setValue('');
    }

    // The index of the next option whose label starts with the typed text, searching
    // from the active one. Typing the same letter again cycles through its options.
    matchTyped(char) {
      const now = Date.now();
      this.typed = now - this.typedAt > TYPEAHEAD_RESET_MS ? char : this.typed + char;
      this.typedAt = now;
      const repeated = this.typed.split('').every((c) => c === this.typed[0]);
      const search = (repeated ? this.typed[0] : this.typed).toLocaleLowerCase();
      const count = this.options.length;
      const start = repeated || this.activeIndex < 0 ? this.activeIndex + 1 : this.activeIndex;
      for (let n = 0; n < count; n += 1) {
        const i = (start + n + count) % count;
        if (this.options[i].label.toLocaleLowerCase().indexOf(search) === 0) return i;
      }
      return -1;
    }

    isTypeahead(event) {
      return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    }

    onButtonKey(event) {
      // Enter and Space open through the button's own click.
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      } else if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close(true);
      } else if (this.isTypeahead(event) && event.key !== ' ') {
        event.preventDefault();
        this.open();
        const match = this.matchTyped(event.key);
        if (match > -1) this.setActive(match, 'center');
      }
    }

    onListKey(event) {
      const last = this.options.length - 1;
      const move = (index) => {
        event.preventDefault();
        this.setActive(Math.min(Math.max(index, 0), last), 'nearest');
      };
      switch (event.key) {
        case 'ArrowDown':
          return move(this.activeIndex + 1);
        case 'ArrowUp':
          return move(this.activeIndex - 1);
        case 'Home':
          return move(0);
        case 'End':
          return move(last);
        case 'PageDown':
          return move(this.activeIndex + PAGE_STEP);
        case 'PageUp':
          return move(this.activeIndex - PAGE_STEP);
        case 'Enter':
          event.preventDefault();
          return this.select(this.activeIndex);
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          return this.close(true);
        case 'Tab':
          // Let focus move on; just close.
          return this.close(false);
        default:
          break;
      }
      if (event.key === ' ' && (!this.typed || Date.now() - this.typedAt > TYPEAHEAD_RESET_MS)) {
        event.preventDefault();
        this.select(this.activeIndex);
      } else if (this.isTypeahead(event)) {
        event.preventDefault();
        const match = this.matchTyped(event.key);
        if (match > -1) this.setActive(match, 'nearest');
      }
    }
  }

  class CreatorsApply {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-creators-apply-form]');
      this.formWrap = root.querySelector('[data-creators-apply-form-wrap]');
      this.loading = root.querySelector('[data-creators-apply-loading]');
      this.closed = root.querySelector('[data-creators-apply-closed]');
      this.success = root.querySelector('[data-creators-apply-success]');
      this.submitBtn = this.form.querySelector('[data-submit]');
      this.summary = this.form.querySelector('[data-summary]');
      this.summaryText = this.form.querySelector('[data-summary-text]');
      this.retryBtn = this.form.querySelector('[data-retry]');
      this.progressBox = this.form.querySelector('[data-progress]');
      this.progressText = this.form.querySelector('[data-progress-text]');
      this.progressMeter = this.form.querySelector('[data-progress-meter]');
      this.progressBar = this.form.querySelector('[data-progress-bar]');
      this.progressLive = this.form.querySelector('[data-progress-live]');
      this.socialsList = this.form.querySelector('[data-socials-list]');
      this.socialAddBtn = this.form.querySelector('[data-social-add]');
      this.socialTemplate = root.querySelector('[data-template-social]');
      this.fileTemplate = root.querySelector('[data-template-file]');

      const stringsTag = root.querySelector('[data-creators-apply-strings]');
      this.strings = stringsTag ? JSON.parse(stringsTag.textContent) : {};
      this.lang = document.documentElement.lang || undefined;
      this.apiBase = (root.getAttribute('data-api-base') || '').trim().replace(/\/+$/, '');

      this.uid = 0;
      this.busy = false;
      // Controls are disabled while busy and cannot take focus until it ends.
      this.pendingFocus = null;
      this.xhrs = [];
      this.screenshots = [];
      this.mediaPack = null;
      // Bumped on every file change. A session started at an older version is stale.
      this.filesVersion = 0;
      // { creatorId, items, files, version, uploaded } once init has succeeded.
      this.session = null;
      // The social rows in the order they were sent, so socials.<i> maps back to a row.
      this.sentSocialRows = [];
      // Serialises file checks, which are async.
      this.fileQueue = Promise.resolve();

      this.onBeforeUnload = (event) => {
        event.preventDefault();
        event.returnValue = '';
      };

      this.buildCountries();
      this.buildBikePicker();
      this.addSocialRow(false);
      this.bind();
      this.checkStatus();
    }

    // ---------- setup ----------

    buildCountries() {
      const select = this.form.querySelector('[data-country-select]');
      let names = null;
      try {
        names = new Intl.DisplayNames(this.lang ? [this.lang] : undefined, { type: 'region' });
      } catch (e) {
        try {
          names = new Intl.DisplayNames(undefined, { type: 'region' });
        } catch (e2) {
          names = null;
        }
      }
      let collator;
      try {
        collator = new Intl.Collator(this.lang);
      } catch (e) {
        collator = new Intl.Collator();
      }
      const nameOf = (code) => {
        try {
          return (names && names.of(code)) || code;
        } catch (e) {
          return code;
        }
      };

      const fragment = document.createDocumentFragment();
      COUNTRY_CODES.map((code) => ({ code, name: nameOf(code) }))
        .sort((a, b) => collator.compare(a.name, b.name))
        .forEach(({ code, name }) => {
          const option = document.createElement('option');
          option.value = code;
          option.textContent = name;
          fragment.appendChild(option);
        });
      select.appendChild(fragment);
    }

    buildBikePicker() {
      const field = this.field('bike_model');
      let models = [];
      try {
        models = JSON.parse(field.querySelector('[data-bike-picker]').getAttribute('data-bike-models') || '[]');
      } catch (e) {
        models = [];
      }
      if (!Array.isArray(models)) models = [];
      models = models.filter((model) => typeof model === 'string' && model !== '');
      const button = field.querySelector('[data-bike-button]');
      // A change event, so the form's "clear the error when edited" handler sees it.
      this.bikePicker = new BikePicker(field, models, this.strings.bikeOther, () =>
        button.dispatchEvent(new Event('change', { bubbles: true }))
      );
    }

    bind() {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.retryBtn.addEventListener('click', () => this.submit());
      this.socialAddBtn.addEventListener('click', () => this.addSocialRow(true));
      this.form.addEventListener('reset', () => this.bikePicker.reset());

      this.form.querySelectorAll('[data-counted]').forEach((textarea) => {
        const update = () => this.updateCounter(textarea);
        textarea.addEventListener('input', update);
        update();
      });

      // Clear a field's error as soon as it is edited.
      this.form.addEventListener('input', (event) => this.clearErrorFor(event.target));
      this.form.addEventListener('change', (event) => this.clearErrorFor(event.target));

      [KIND_SCREENSHOT, KIND_MEDIA_PACK].forEach((kind) => {
        const field = this.field(kind);
        const input = field.querySelector('[data-file-input]');
        field.querySelector('[data-file-pick]').addEventListener('click', () => input.click());
        input.addEventListener('change', () => {
          const files = Array.from(input.files || []);
          input.value = '';
          if (!files.length) return;
          this.fileQueue = this.fileQueue.then(() =>
            kind === KIND_SCREENSHOT ? this.addScreenshots(files) : this.setMediaPack(files[0])
          );
        });
      });
    }

    checkStatus() {
      this.loading.hidden = false;
      let settled = false;
      const done = (open) => {
        if (settled) return;
        settled = true;
        this.loading.hidden = true;
        if (open === false) this.showClosed(false);
        else this.formWrap.hidden = false;
      };
      if (!this.apiBase) return done(true);

      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(() => {
        if (controller) controller.abort();
        done(true);
      }, STATUS_TIMEOUT_MS);

      fetch(this.apiBase + '/api/applications/status', {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        signal: controller ? controller.signal : undefined
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => done(data && data.open === false ? false : true))
        .catch(() => done(true))
        .finally(() => clearTimeout(timer));
    }

    // ---------- helpers ----------

    field(name) {
      return this.form.querySelector(`[data-field="${name}"]`);
    }

    nextId(suffix) {
      this.uid += 1;
      return `${this.form.id}-${suffix}-${this.uid}`;
    }

    formatSize(bytes) {
      const mb = Math.max(bytes / MB, 0.1);
      let number;
      try {
        number = new Intl.NumberFormat(this.lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(mb);
      } catch (e) {
        number = mb.toFixed(1);
      }
      return fill(this.strings.fileSize, { size: number });
    }

    updateCounter(textarea) {
      const counter = textarea.parentElement.querySelector('[data-counter]');
      if (!counter) return;
      counter.textContent = fill(this.strings.characterCount, {
        count: textarea.value.length,
        max: textarea.maxLength
      });
    }

    // ---------- social links ----------

    socialRows() {
      return Array.from(this.socialsList.querySelectorAll('[data-social-row]'));
    }

    addSocialRow(focus) {
      if (this.socialRows().length >= LIMITS.socialsMax) return;
      const row = this.socialTemplate.content.firstElementChild.cloneNode(true);
      const platform = row.querySelector('[data-social-platform]');
      const url = row.querySelector('[data-social-url]');
      const error = row.querySelector('[data-error]');
      platform.id = this.nextId('platform');
      url.id = this.nextId('url');
      error.id = this.nextId('social-error');
      row.querySelector('[data-label-platform]').htmlFor = platform.id;
      row.querySelector('[data-label-url]').htmlFor = url.id;
      platform.setAttribute('aria-describedby', error.id);
      url.setAttribute('aria-describedby', error.id);
      row.querySelector('[data-social-remove]').addEventListener('click', () => this.removeSocialRow(row));
      this.socialsList.appendChild(row);
      this.updateSocialRows();
      if (focus) platform.focus();
    }

    removeSocialRow(row) {
      const rows = this.socialRows();
      if (rows.length <= 1) return;
      const index = rows.indexOf(row);
      row.remove();
      this.updateSocialRows();
      const remaining = this.socialRows();
      const next = remaining[Math.min(index, remaining.length - 1)];
      next.querySelector('[data-social-url]').focus();
    }

    updateSocialRows() {
      const rows = this.socialRows();
      rows.forEach((row, i) => {
        const count = i + 1;
        row.querySelector('[data-label-platform]').textContent = fill(this.strings.platformNumbered, { count });
        row.querySelector('[data-label-url]').textContent = fill(this.strings.urlNumbered, { count });
        const remove = row.querySelector('[data-social-remove]');
        remove.setAttribute('aria-label', fill(this.strings.removeSocialNumbered, { count }));
        remove.hidden = rows.length <= 1;
      });
      this.socialAddBtn.hidden = rows.length >= LIMITS.socialsMax;
    }

    // ---------- files ----------

    async addScreenshots(files) {
      const field = this.field(KIND_SCREENSHOT);
      this.clearFieldError(field);
      const errors = [];
      for (const file of files) {
        if (this.screenshots.length >= LIMITS.screenshotsMax) {
          errors.push(fill(this.strings.screenshotsTooMany, { max: LIMITS.screenshotsMax }));
          break;
        }
        const name = file.name;
        if (SCREENSHOT_TYPES.indexOf(file.type) === -1) {
          errors.push(fill(this.strings.screenshotType, { name }));
        } else if (file.size > LIMITS.screenshotBytes) {
          errors.push(fill(this.strings.screenshotSize, { name, max: LIMITS.screenshotBytes / MB }));
        } else if (file.size === 0 || !(await matchesSignature(file, file.type))) {
          errors.push(fill(this.strings.fileContent, { name }));
        } else {
          this.screenshots.push(this.renderFile(field, file, KIND_SCREENSHOT));
          this.filesVersion += 1;
        }
      }
      this.syncFilePickers();
      if (errors.length) this.setFieldError(field, errors.join(' '));
      // The picker hides at the limit; keep keyboard focus inside the field.
      const picker = field.querySelector('[data-file-pick]');
      if (picker.hidden && this.screenshots.length && (document.activeElement === picker || document.activeElement === document.body)) {
        this.screenshots[this.screenshots.length - 1].el.querySelector('[data-file-remove]').focus();
      }
    }

    async setMediaPack(file) {
      const field = this.field(KIND_MEDIA_PACK);
      this.clearFieldError(field);
      const name = file.name;
      let error = null;
      if (file.type !== MEDIA_PACK_TYPE) {
        error = fill(this.strings.mediaPackType, { name });
      } else if (file.size > LIMITS.mediaPackBytes) {
        error = fill(this.strings.mediaPackSize, { name, max: LIMITS.mediaPackBytes / MB });
      } else if (file.size === 0 || !(await matchesSignature(file, file.type))) {
        error = fill(this.strings.fileContent, { name });
      }
      if (error) {
        this.setFieldError(field, error);
        return;
      }
      if (this.mediaPack) this.discardFile(this.mediaPack);
      this.mediaPack = this.renderFile(field, file, KIND_MEDIA_PACK);
      this.filesVersion += 1;
      this.syncFilePickers();
      this.mediaPack.el.querySelector('[data-file-replace]').focus();
    }

    renderFile(field, file, kind) {
      const el = this.fileTemplate.content.firstElementChild.cloneNode(true);
      const item = { kind, file, el, url: null };
      const thumb = el.querySelector('[data-file-thumb]');
      if (kind === KIND_SCREENSHOT) {
        item.url = URL.createObjectURL(file);
        thumb.src = item.url;
      } else {
        thumb.remove();
      }
      el.querySelector('[data-file-name]').textContent = file.name;
      el.querySelector('[data-file-size]').textContent = this.formatSize(file.size);

      const remove = el.querySelector('[data-file-remove]');
      remove.setAttribute('aria-label', fill(this.strings.removeFileNamed, { name: file.name }));
      remove.addEventListener('click', () => this.removeFile(item));

      if (kind === KIND_MEDIA_PACK) {
        const replace = el.querySelector('[data-file-replace]');
        replace.hidden = false;
        replace.setAttribute('aria-label', fill(this.strings.replaceFileNamed, { name: file.name }));
        replace.addEventListener('click', () => field.querySelector('[data-file-input]').click());
      }

      field.querySelector('[data-file-list]').appendChild(el);
      return item;
    }

    discardFile(item) {
      if (item.url) URL.revokeObjectURL(item.url);
      item.el.remove();
    }

    removeFile(item) {
      if (this.busy) return;
      const field = this.field(item.kind);
      this.clearFieldError(field);
      let focusTarget = null;
      if (item.kind === KIND_SCREENSHOT) {
        const index = this.screenshots.indexOf(item);
        this.screenshots.splice(index, 1);
        const next = this.screenshots[index] || this.screenshots[index - 1];
        if (next) focusTarget = next.el.querySelector('[data-file-remove]');
      } else {
        this.mediaPack = null;
      }
      this.discardFile(item);
      this.filesVersion += 1;
      this.syncFilePickers();
      (focusTarget || field.querySelector('[data-file-pick]')).focus();
    }

    syncFilePickers() {
      this.field(KIND_SCREENSHOT).querySelector('[data-file-pick]').hidden =
        this.screenshots.length >= LIMITS.screenshotsMax;
      this.field(KIND_MEDIA_PACK).querySelector('[data-file-pick]').hidden = !!this.mediaPack;
    }

    fileItems() {
      return this.screenshots.concat(this.mediaPack ? [this.mediaPack] : []);
    }

    // ---------- errors ----------

    setFieldError(target, message) {
      const error = target.querySelector(':scope > [data-error]');
      error.textContent = message;
      error.hidden = false;
      const controls = target.matches('[data-social-row]')
        ? target.querySelectorAll('select, input')
        : target.querySelectorAll(':scope [data-input]');
      controls.forEach((control) => {
        control.setAttribute('aria-invalid', 'true');
        if (control.type !== 'checkbox') control.classList.add('error');
      });
      if (target.matches('[data-field="consent"]')) {
        target.querySelector('label').classList.add('error');
      }
    }

    clearFieldError(target) {
      const error = target.querySelector(':scope > [data-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      target.querySelectorAll('[aria-invalid]').forEach((control) => {
        control.removeAttribute('aria-invalid');
        control.classList.remove('error');
      });
      target.querySelectorAll('label.error').forEach((label) => label.classList.remove('error'));
    }

    clearErrorFor(control) {
      if (!control || !control.closest) return;
      const target = control.closest('[data-social-row]') || control.closest('[data-field]');
      if (target && target.querySelector(':scope > [data-error]:not([hidden])')) this.clearFieldError(target);
    }

    clearAllErrors() {
      this.form.querySelectorAll('[data-field], [data-social-row]').forEach((target) => this.clearFieldError(target));
    }

    focusTarget(target) {
      if (target.matches('[data-social-row]')) {
        return target.querySelector('[data-social-url]');
      }
      const candidates = target.querySelectorAll(
        '[data-input], [data-social-url], [data-file-pick], [data-file-replace], [data-file-remove]'
      );
      return Array.from(candidates).find((el) => !el.hidden) || null;
    }

    focus(el) {
      if (!el) return;
      if (this.busy) this.pendingFocus = el;
      else el.focus();
    }

    // errors: [{ target, message }] — shows each, the summary, and focuses the first.
    reportErrors(errors, summaryMessage) {
      errors.forEach(({ target, message }) => this.setFieldError(target, message));
      this.showSummary(summaryMessage || this.strings.summary, false);
      const first = errors
        .map(({ target }) => target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))[0];
      this.focus(first && this.focusTarget(first));
    }

    showSummary(message, retry) {
      this.summaryText.textContent = message;
      this.retryBtn.hidden = !retry;
      this.summary.hidden = false;
    }

    hideSummary() {
      this.summary.hidden = true;
      this.summaryText.textContent = '';
      this.retryBtn.hidden = true;
    }

    showError(message) {
      this.showSummary(message, true);
      this.focus(this.retryBtn);
    }

    // ---------- client validation (mirrors the server rules) ----------

    validate() {
      const s = this.strings;
      const errors = [];
      const value = (name) => this.form.elements[name].value.trim();
      const add = (target, message) => errors.push({ target, message });

      const name = value('full_name');
      if (!name) add(this.field('full_name'), s.nameRequired);
      else if (name.length > LIMITS.name) add(this.field('full_name'), fill(s.nameTooLong, { max: LIMITS.name }));

      const email = value('email');
      if (!email) add(this.field('email'), s.emailRequired);
      else if (email.length > LIMITS.email || !EMAIL_RE.test(email)) add(this.field('email'), s.emailInvalid);

      if (!COUNTRY_SET.has(this.form.elements.country.value)) add(this.field('country'), s.countryRequired);

      if (!this.bikePicker.value) add(this.field('bike_model'), s.bikeRequired);

      const introduction = value('introduction');
      if (!introduction) add(this.field('introduction'), s.introductionRequired);
      else if (introduction.length > LIMITS.introduction) {
        add(this.field('introduction'), fill(s.introductionTooLong, { max: LIMITS.introduction }));
      }

      if (value('gear_interest').length > LIMITS.gear) {
        add(this.field('gear_interest'), fill(s.gearTooLong, { max: LIMITS.gear }));
      }

      const rows = this.socialRows();
      if (rows.length < 1 || rows.length > LIMITS.socialsMax) {
        add(this.field('socials'), fill(s.socialsCount, { max: LIMITS.socialsMax }));
      }
      rows.forEach((row) => {
        const platform = row.querySelector('[data-social-platform]').value;
        const url = row.querySelector('[data-social-url]').value.trim();
        if (PLATFORMS.indexOf(platform) === -1) add(row, s.socialPlatformInvalid);
        else if (!url) add(row, s.socialUrlRequired);
        else if (url.length > LIMITS.url) add(row, fill(s.socialUrlTooLong, { max: LIMITS.url }));
        else if (!isHttpUrl(url)) add(row, s.socialUrlInvalid);
      });

      if (!this.screenshots.length) add(this.field(KIND_SCREENSHOT), s.screenshotsRequired);
      else if (this.screenshots.length > LIMITS.screenshotsMax) {
        add(this.field(KIND_SCREENSHOT), fill(s.screenshotsTooMany, { max: LIMITS.screenshotsMax }));
      }

      if (!this.mediaPack) add(this.field(KIND_MEDIA_PACK), s.mediaPackRequired);

      if (!this.form.elements.consent.checked) add(this.field('consent'), s.consentRequired);

      return errors;
    }

    collectFields() {
      const value = (name) => this.form.elements[name].value.trim();
      this.sentSocialRows = this.socialRows();
      return {
        full_name: value('full_name'),
        email: value('email'),
        country: this.form.elements.country.value,
        bike_model: this.bikePicker.value,
        introduction: value('introduction'),
        gear_interest: value('gear_interest'),
        socials: this.sentSocialRows.map((row) => ({
          platform: row.querySelector('[data-social-platform]').value,
          url: row.querySelector('[data-social-url]').value.trim()
        })),
        consent: true
      };
    }

    // ---------- server error mapping ----------

    // A server field path to the element its error belongs on, or null.
    targetForPath(path) {
      const parts = String(path || '').split('.');
      const s = this.strings;
      switch (parts[0]) {
        case 'full_name':
          return { target: this.field('full_name'), message: s.nameRequired };
        case 'email':
          return { target: this.field('email'), message: s.emailInvalid };
        case 'country':
          return { target: this.field('country'), message: s.countryRequired };
        case 'introduction':
          return { target: this.field('introduction'), message: s.fieldInvalid };
        case 'gear_interest':
          return { target: this.field('gear_interest'), message: s.fieldInvalid };
        case 'consent':
          return { target: this.field('consent'), message: s.consentRequired };
        case 'socials': {
          const row = /^\d+$/.test(parts[1] || '') ? this.sentSocialRows[Number(parts[1])] : null;
          if (row && row.isConnected) {
            return { target: row, message: parts[2] === 'platform' ? s.socialPlatformInvalid : s.socialUrlInvalid };
          }
          return { target: this.field('socials'), message: fill(s.socialsCount, { max: LIMITS.socialsMax }) };
        }
        default:
          return null;
      }
    }

    // files / files.<i>.* to the file field it belongs to. `items` is the list sent.
    targetForFilePath(path, items) {
      const parts = String(path).split('.');
      const item = /^\d+$/.test(parts[1] || '') ? items[Number(parts[1])] : null;
      if (item) return { target: this.field(item.kind), message: this.strings.fileServer };
      return null;
    }

    // ---------- progress ----------

    setProgress(text, percent) {
      this.progressBox.hidden = false;
      this.progressText.textContent = text;
      this.progressBar.style.width = `${percent}%`;
      this.progressMeter.setAttribute('aria-valuenow', String(percent));
    }

    announce(text) {
      if (this.progressLive.textContent !== text) this.progressLive.textContent = text;
    }

    setBusy(busy) {
      this.busy = busy;
      // aria-disabled, not disabled: the theme paints [disabled] buttons light grey with
      // !important, which would hide the btn--loading spinner. `busy` blocks resubmits.
      this.submitBtn.setAttribute('aria-disabled', busy ? 'true' : 'false');
      this.submitBtn.classList.toggle('btn--loading', busy);
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.form
        .querySelectorAll('[data-file-pick], [data-file-remove], [data-file-replace], [data-social-add], [data-social-remove], [data-retry]')
        .forEach((button) => {
          button.disabled = busy;
        });
      if (busy) {
        window.addEventListener('beforeunload', this.onBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        this.progressBox.hidden = true;
        if (this.pendingFocus) {
          const el = this.pendingFocus;
          this.pendingFocus = null;
          el.focus();
        }
      }
    }

    // ---------- network ----------

    async post(endpoint, body) {
      try {
        const response = await fetch(`${this.apiBase}/api/applications/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          credentials: 'omit',
          cache: 'no-store'
        });
        let data = null;
        try {
          data = await response.json();
        } catch (e) {
          data = null;
        }
        return { status: response.status, ok: response.ok, data };
      } catch (e) {
        return { status: 0, ok: false, data: null, network: true };
      }
    }

    uploadOne(upload, item, onProgress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        this.xhrs.push(xhr);
        const finish = (fn) => {
          this.xhrs = this.xhrs.filter((other) => other !== xhr);
          fn();
        };
        xhr.open('PUT', upload.upload_url);
        xhr.setRequestHeader('Content-Type', item.file.type);
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.setRequestHeader('Cache-Control', 'max-age=3600');
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) onProgress(Math.min(event.loaded, item.file.size));
        };
        xhr.onload = () =>
          finish(() => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(String(xhr.status)))));
        xhr.onerror = () => finish(() => reject(new Error('network')));
        xhr.onabort = () => finish(() => reject(new Error('abort')));
        xhr.ontimeout = () => finish(() => reject(new Error('timeout')));
        xhr.send(item.file);
      });
    }

    async uploadAll(session) {
      const items = session.items;
      const total = items.reduce((sum, item) => sum + item.file.size, 0) || 1;
      const loaded = items.map(() => 0);
      let next = 0;
      let failed = false;
      let lastAnnounced = -1;

      const report = () => {
        const percent = Math.min(100, Math.floor((loaded.reduce((a, b) => a + b, 0) / total) * 100));
        const text = fill(this.strings.uploading, { percent });
        this.setProgress(text, percent);
        // Screen readers hear every 25%, not every byte.
        const step = Math.floor(percent / 25) * 25;
        if (step !== lastAnnounced) {
          lastAnnounced = step;
          this.announce(fill(this.strings.uploading, { percent: step }));
        }
      };

      const fileBar = (item, bytes) => {
        const bar = item.el.querySelector('[data-file-bar]');
        bar.hidden = false;
        bar.querySelector('[data-file-bar-fill]').style.width = `${Math.floor((bytes / (item.file.size || 1)) * 100)}%`;
      };

      items.forEach((item) => fileBar(item, 0));
      report();

      const worker = async () => {
        while (!failed && next < items.length) {
          const i = next;
          next += 1;
          try {
            await this.uploadOne(session.uploads[i], items[i], (bytes) => {
              loaded[i] = bytes;
              fileBar(items[i], bytes);
              report();
            });
            loaded[i] = items[i].file.size;
            fileBar(items[i], loaded[i]);
            report();
          } catch (e) {
            if (!failed) {
              failed = true;
              this.xhrs.slice().forEach((xhr) => xhr.abort());
            }
          }
        }
      };

      const workers = [];
      for (let w = 0; w < Math.min(UPLOAD_CONCURRENCY, items.length); w += 1) workers.push(worker());
      await Promise.all(workers);
      return !failed;
    }

    resetFileBars() {
      this.fileItems().forEach((item) => {
        const bar = item.el.querySelector('[data-file-bar]');
        bar.hidden = true;
        bar.querySelector('[data-file-bar-fill]').style.width = '0%';
      });
    }

    // ---------- submit ----------

    async submit() {
      if (this.busy) return;
      this.hideSummary();
      this.clearAllErrors();
      const errors = this.validate();
      if (errors.length) {
        this.reportErrors(errors);
        return;
      }
      const fields = this.collectFields();
      this.setBusy(true);
      let finished = false;
      try {
        finished = await this.run(fields);
      } finally {
        if (!finished) this.setBusy(false);
      }
    }

    // Returns true when the form has been replaced (success or closed).
    async run(fields) {
      const s = this.strings;

      // ---- 1. init, unless a session for exactly these files exists ----
      if (!this.session || this.session.version !== this.filesVersion) {
        this.session = null;
        this.resetFileBars();
        this.setProgress(s.preparing, 0);
        this.announce(s.preparing);

        const items = this.fileItems();
        const init = await this.post('init', {
          files: items.map((item) => ({
            kind: item.kind,
            name: item.file.name.slice(0, 255) || item.kind,
            mime_type: item.file.type,
            size_bytes: item.file.size
          }))
        });

        if (init.network) {
          this.showError(s.network);
          return false;
        }
        if (init.status === 503) {
          this.showClosed(true);
          return true;
        }
        if (init.status === 400 && init.data && Array.isArray(init.data.fields)) {
          const mapped = init.data.fields.map((f) => this.targetForFilePath(f.path, items)).filter(Boolean);
          if (mapped.length) {
            this.reportErrors(mapped);
            return false;
          }
        }
        const data = init.data;
        const valid =
          init.status === 200 &&
          data &&
          typeof data.creator_id === 'string' &&
          Array.isArray(data.uploads) &&
          data.uploads.length === items.length &&
          data.uploads.every(
            (upload, i) =>
              upload &&
              upload.kind === items[i].kind &&
              typeof upload.path === 'string' &&
              typeof upload.upload_url === 'string'
          );
        if (!valid) {
          this.showError(s.generic);
          return false;
        }

        this.session = {
          creatorId: data.creator_id,
          items,
          uploads: data.uploads,
          files: items.map((item, i) => ({
            kind: item.kind,
            path: data.uploads[i].path,
            mime_type: item.file.type,
            size_bytes: item.file.size
          })),
          version: this.filesVersion,
          uploaded: false
        };
      }

      const session = this.session;

      // ---- 2. upload every file to its signed URL ----
      if (!session.uploaded) {
        const ok = await this.uploadAll(session);
        if (!ok) {
          // A signed URL is single-use and we cannot tell which uploads landed.
          this.session = null;
          this.showError(s.uploadFailed);
          return false;
        }
        session.uploaded = true;
      }

      // ---- 3. complete ----
      this.setProgress(s.sending, 100);
      this.announce(s.sending);
      const complete = await this.post(
        'complete',
        Object.assign({ creator_id: session.creatorId }, fields, { files: session.files })
      );

      if (complete.network) {
        // Unknown whether it landed; resending complete answers 409 if it did.
        this.showError(s.network);
        return false;
      }
      if (complete.ok || complete.status === 409) {
        this.showSuccess();
        return true;
      }
      if (complete.status === 503) {
        this.showClosed(true);
        return true;
      }
      if (complete.status === 400 && complete.data && Array.isArray(complete.data.fields)) {
        const list = complete.data.fields;
        const fileErrors = list.filter((f) => String(f.path || '').indexOf('files') === 0);
        if (fileErrors.length) {
          // The server deleted the uploads: the next send starts again at init.
          const mapped = fileErrors.map((f) => this.targetForFilePath(f.path, session.items)).filter(Boolean);
          this.session = null;
          this.resetFileBars();
          if (mapped.length) this.reportErrors(mapped, s.filesReupload);
          else this.showError(s.filesReupload);
          return false;
        }
        const mapped = list.map((f) => this.targetForPath(f.path));
        if (mapped.length && mapped.every(Boolean)) {
          // Uploads are kept: fixing the fields and sending again goes straight to complete.
          this.reportErrors(mapped);
          return false;
        }
        if (list.some((f) => String(f.path || '').split('.')[0] === 'creator_id')) this.session = null;
        this.showError(s.generic);
        return false;
      }
      if (complete.status === 500) {
        // Nothing from this attempt was kept: start again at init.
        this.session = null;
        this.resetFileBars();
      }
      this.showError(s.generic);
      return false;
    }

    // ---------- end states ----------

    // Drops everything the creator entered: object URLs, files, the form itself.
    discardForm() {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
      this.xhrs.slice().forEach((xhr) => xhr.abort());
      this.fileItems().forEach((item) => this.discardFile(item));
      this.screenshots = [];
      this.mediaPack = null;
      this.session = null;
      this.sentSocialRows = [];
      // The reset handler closes the bike picker and clears its value.
      this.form.reset();
      this.formWrap.remove();
    }

    showSuccess() {
      this.discardForm();
      this.success.hidden = false;
      this.success.focus();
    }

    showClosed(focus) {
      this.discardForm();
      this.closed.hidden = false;
      if (focus) this.closed.focus();
    }
  }

  const start = () => {
    document.querySelectorAll('[data-creators-apply]').forEach((root) => {
      if (root.creatorsApply) return;
      root.creatorsApply = new CreatorsApply(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  // Theme editor: re-run when the section is re-rendered.
  document.addEventListener('shopify:section:load', start);
})();
